import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { GetStaticProps, InferGetStaticPropsType } from "next";

import React from "react";
import { FaLanguage, FaTags, FaListAlt, FaUserCheck } from "react-icons/fa";
import Sticky from "react-stickynode";
import {
  IWorkshopWithMentorModel,
  ProfileHeader,
  IMentorModel,
  MentorDescription,
  formatDate,
} from "ui";

import { PageContainer } from "../../src/components/ui/PageContainer";
import { WorkshopGrid } from "../../src/components/WorkshopGrid";
import {
  getAllMentors,
  getFilteredWorkshopsWithMentor,
  getMentor,
} from "../../src/utils/firebase";

import { DetailsCard } from "../../src/components/Mentor/BookingRequestCard/DetailsCard";
import { Layout } from "../../src/components/Layout";

const MentorDetailPage = ({
  mentor,
  workshopsFromMentor,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  return (
    <Layout
      pageTitle={`mentorlab | Mentor | ${mentor.firstName} ${mentor.lastName}`}
      pageDescription={`${mentor.bio}`}
      og={{
        currentRelativeUrlPath: `/mentors/${mentor.id}`,
        siteName: `mentorlab | Mentor | ${mentor.firstName} ${mentor.lastName}`,
      }}
    >
      <PageContainer>
        <Container maxW={"7xl"}>
          <Stack direction={["column", "column", "column", "row", "row"]}>
            <Stack w={["100%", "100%", "100%", "65%", "65%"]}>
              <ProfileHeader
                userData={{
                  firstName: mentor.firstName,
                  lastName: mentor.lastName,
                  jobTitle: mentor.jobTitle,
                  company: mentor.company,
                  city: mentor.city,
                  imageUrl: mentor.imageUrl,
                  linkedIn: mentor.linkedIn,
                  xing: mentor.xing,
                }}
                isPreview={false}
              />
              <MentorDescription
                bio={mentor.bio}
                experience={mentor.experience}
                teaching={mentor.teaching}
                facts={mentor.facts}
              />
              <Divider pt={10} />
              <VStack spacing={7} alignItems={"flex-start"} pt={10}>
                <Flex alignItems={"center"}>
                  <FaLanguage size={30} />
                  <Box ml={5}>
                    <Text fontWeight={700}>Sprachen</Text>
                    <Text>
                      {mentor.languages.map((language, index) => {
                        if (mentor.languages.length - 1 === index) {
                          return language.name;
                        }
                        return `${language.name}, `;
                      })}
                    </Text>
                  </Box>
                </Flex>
                {mentor.categories !== null ? (
                  <Flex alignItems={"center"}>
                    <FaListAlt size={30} />
                    <Box ml={5}>
                      <Text fontWeight={700}>Kategorien</Text>
                      <Text>{mentor.categories.join(", ")}</Text>
                    </Box>
                  </Flex>
                ) : null}

                <Flex alignItems={"center"}>
                  <FaTags size={30} />
                  <Box ml={5}>
                    <Text fontWeight={700}>Hashtags</Text>
                    <Text>
                      {mentor.hashtags.map((hashtag, index) => {
                        if (mentor.hashtags.length - 1 === index) {
                          return hashtag.name;
                        }
                        return `${hashtag.name}, `;
                      })}
                    </Text>
                  </Box>
                </Flex>
              </VStack>
            </Stack>
            <Stack w={["100%", "100%", "100%", "35%", "35%"]}>
              <Sticky
                enabled={true}
                top={"#details-top"}
                bottomBoundary={"#details-bottom"}
              >
                <DetailsCard
                  mentorId={mentor.id}
                  mentorName={`${mentor.firstName} ${mentor.lastName}`}
                />
              </Sticky>
            </Stack>
          </Stack>
        </Container>
        <Divider my={10} id="details-bottom" />
        <Container maxW="7xl" marginTop={12}>
          <Heading as="h2" size="lg" mb={10}>
            Workshops des Mentors
          </Heading>
        </Container>
        <WorkshopGrid workshops={workshopsFromMentor} />
      </PageContainer>
    </Layout>
  );
};

export default MentorDetailPage;

export const getStaticProps: GetStaticProps<{
  mentor: IMentorModel;
  workshopsFromMentor: IWorkshopWithMentorModel[];
}> = async ({ params }) => {
  // @ts-ignore
  const { id } = params;
  const mentor = await getMentor(id as string);

  if (!mentor || !mentor.active) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    };
  }

  let workshopsFromMentor: IWorkshopWithMentorModel[] | undefined;

  workshopsFromMentor = await getFilteredWorkshopsWithMentor({
    type: "mentor",
    query: id as string,
    onlyActive: true,
  });
  if (!workshopsFromMentor) {
    workshopsFromMentor = [];
  }

  return {
    props: {
      mentor,
      workshopsFromMentor,
    },
    revalidate: 10,
  };
};

export async function getStaticPaths() {
  const mentors = await getAllMentors();
  if (!mentors) {
    return { paths: [], fallback: "blocking" };
  }
  const paths = mentors.map((mentor) => ({
    params: { id: mentor.id },
  }));

  return { paths, fallback: "blocking" };
}
